import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  EyeIcon,
  EyeSlashIcon,
  HeartIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Form submitted:', formData)
  }

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8">
        {/* Left Side - Branding */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          className="hidden lg:flex flex-col justify-center p-12 card-glass"
        >
          <motion.div
            initial={{ scale: 0.5 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 mb-6">
              <HeartIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-5xl font-display font-bold mb-4">
              Welcome to
              <span className="gradient-text block">ConnectHub</span>
            </h1>
            <p className="text-xl text-white/70 mb-8">
              Where meaningful connections begin and stories unfold
            </p>
          </motion.div>

          <div className="space-y-6">
            <FeatureItem
              icon={HeartIcon}
              title="Smart Matching"
              description="Advanced algorithms to find your perfect match"
            />
            <FeatureItem
              icon={SparklesIcon}
              title="Share Your Story"
              description="Create and share your journey with the world"
            />
            <FeatureItem
              icon={UserIcon}
              title="Verified Profiles"
              description="Connect with real people in a safe environment"
            />
          </div>
        </motion.div>

        {/* Right Side - Auth Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center"
        >
          <div className="w-full card-glass p-8 md:p-12">
            {/* Toggle Buttons */}
            <div className="flex space-x-2 mb-8 p-1 bg-white/5 rounded-xl">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                  isLogin
                    ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                  !isLogin
                    ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Sign Up
              </button>
            </div>

            <h2 className="text-3xl font-bold mb-2">
              {isLogin ? 'Welcome Back!' : 'Create Account'}
            </h2>
            <p className="text-white/70 mb-8">
              {isLogin
                ? 'Sign in to continue your journey'
                : 'Start your ConnectHub experience today'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field (Sign Up Only) */}
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="block text-sm font-medium mb-2">Full Name</label>
                  <div className="relative">
                    <UserIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-glass w-full pl-12"
                      placeholder="John Doe"
                      required={!isLogin}
                    />
                  </div>
                </motion.div>
              )}

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-glass w-full pl-12"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-glass w-full pl-12 pr-12"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password (Sign Up Only) */}
              {!isLogin && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="block text-sm font-medium mb-2">Confirm Password</label>
                  <div className="relative">
                    <LockClosedIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="input-glass w-full pl-12"
                      placeholder="••••••••"
                      required={!isLogin}
                    />
                  </div>
                </motion.div>
              )}

              {/* Remember Me / Forgot Password */}
              {isLogin && (
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" className="rounded border-white/30 bg-white/10" />
                    <span className="text-white/70">Remember me</span>
                  </label>
                  <a href="#" className="text-primary-400 hover:underline">
                    Forgot password?
                  </a>
                </div>
              )}

              {/* Terms & Conditions (Sign Up Only) */}
              {!isLogin && (
                <label className="flex items-start space-x-2 text-sm text-white/70 cursor-pointer">
                  <input type="checkbox" className="mt-1 rounded border-white/30 bg-white/10" required />
                  <span>
                    I agree to the{' '}
                    <a href="#" className="text-primary-400 hover:underline">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-primary-400 hover:underline">
                      Privacy Policy
                    </a>
                  </span>
                </label>
              )}

              {/* Submit Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className="btn-primary w-full text-lg py-4"
              >
                {isLogin ? 'Sign In' : 'Create Account'}
              </motion.button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-dark-100 text-white/50">Or continue with</span>
              </div>
            </div>

            {/* Social Login */}
            <div className="grid grid-cols-3 gap-4">
              <SocialButton provider="Google" />
              <SocialButton provider="Facebook" />
              <SocialButton provider="Apple" />
            </div>

            {/* Footer Text */}
            <p className="text-center text-sm text-white/70 mt-8">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary-400 hover:underline font-medium"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

interface FeatureItemProps {
  icon: React.ComponentType<any>
  title: string
  description: string
}

const FeatureItem: React.FC<FeatureItemProps> = ({ icon: Icon, title, description }) => (
  <motion.div
    whileHover={{ x: 10 }}
    className="flex items-start space-x-4"
  >
    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-r from-primary-600/20 to-secondary-600/20 flex items-center justify-center">
      <Icon className="h-6 w-6 text-primary-400" />
    </div>
    <div>
      <h3 className="font-bold mb-1">{title}</h3>
      <p className="text-sm text-white/70">{description}</p>
    </div>
  </motion.div>
)

const SocialButton: React.FC<{ provider: string }> = ({ provider }) => (
  <motion.button
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className="btn-glass py-3"
  >
    <span className="text-sm">{provider}</span>
  </motion.button>
)

export default AuthPage